import { FC, useState } from "react";
import { Edit, TaskAlt } from "@mui/icons-material";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getPriorityColor } from "../../brokers/dataGrid/hooks/useBrokerColumns";
import { Broker } from "../../../../api/Api";
import { EditBrokerDialog } from "./editBrokerDialog";
import { canEditBroker, canManageSignOff } from "../../../../auth/permissions";
import { useRoles } from "../../../../auth/useRoles";
import { useEntityBasePath } from "../../../../utils/entityPaths";
import { EditMergerDialog } from "../../../mergers/editMergerDialog";
import { Colors } from "../../../../theme";

type Props = {
  specificBroker: Broker;
};

export const InfoCard: FC<Props> = ({ specificBroker }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const roles = useRoles();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const isMerger = Boolean(specificBroker.isMerger);
  const showEdit = canEditBroker(roles, isMerger);

  const signOffStatus = specificBroker.signOff?.status;
  const signOffButtonLabel =
    signOffStatus === "completed"
      ? "VIEW SIGN OFF"
      : signOffStatus === "in_progress"
        ? "SIGN OFF IN PROGRESS"
        : signOffStatus === "rejected"
          ? "SIGN OFF REJECTED"
          : "COMPLETE SIGN OFF";

  // A signatory VP holds no app role, so the entry has to appear for anyone once a sign-off exists,
  // not only for the people who can start one.
  const showSignOffEntry =
    canManageSignOff(roles, isMerger) || Boolean(signOffStatus);

  const signOffButtonColor =
    signOffStatus === "completed"
      ? Colors.NICE_GREEN
      : signOffStatus === "in_progress" || signOffStatus === "rejected"
        ? Colors.MFT_ORANGE
        : undefined;

  return (
    <>
      <Grid position="relative" container spacing={3} padding={1}>
        <Box
          sx={{
            position: { xs: "static", md: "absolute" },
            top: { md: "16px" },
            right: { md: "16px" },
            bottom: { md: "16px" },
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            justifyContent: { xs: "flex-end", md: "space-between" },
            flexWrap: "wrap",
            gap: 1,
            alignItems: { xs: "center", md: "flex-end" },
            zIndex: 1,
            marginBottom: { xs: 1, md: 0 },
            paddingX: { xs: 1, md: 0 }
          }}
        >
          {showEdit && (
            <Button
              endIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
              variant="outlined"
            >
              EDIT
            </Button>
          )}
          {showSignOffEntry && (
            <Button
              endIcon={<TaskAlt />}
              onClick={() =>
                navigate(`${basePath}/${specificBroker.id}/signoff`)
              }
              variant="contained"
              sx={
                signOffButtonColor
                  ? {
                      backgroundColor: signOffButtonColor,
                      "&:hover": { backgroundColor: signOffButtonColor }
                    }
                  : undefined
              }
            >
              {signOffButtonLabel}
            </Button>
          )}
        </Box>
        <Grid size={{ xs: 12, md: 12 }}>
          <Card elevation={5}>
            <CardContent>
              <Box display="flex">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography variant="h5" sx={{ marginBottom: 2 }}>
                    Basic Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {/* The rest of the merger setup (the Market Access answers and documents)
                        is shown in the Compliance lifecycle. */}
                    {isMerger && (
                      <Box display="flex" gap={3}>
                        <Typography
                          flex="1"
                          variant="caption"
                          color="text.secondary"
                        >
                          Merger Description
                        </Typography>
                        <Typography
                          flex="2.5"
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {specificBroker.mergerDescription || "N/A"}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Trading Team
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificBroker.tradingTeam || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Contracting Entity
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificBroker.contractingEntity ||
                          "There should be something here - contact Tech"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        MFT Contact Person
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificBroker.contactPersonTradingTeam || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Broker Contact Name
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificBroker.brokerContactName || "N/A"}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Broker Contact Email
                      </Typography>
                      <Typography flex="2.5" variant="body2">
                        {specificBroker.brokerContactEmail || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography variant="h5" sx={{ marginBottom: 2 }}>
                    Trading Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Product Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificBroker.productType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Venue
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificBroker.venue || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Settlement Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificBroker.tradingSettlementType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Agreement Type
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificBroker.agreementType || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={3}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Transaction Type
                      </Typography>
                      <Box flex="3">
                        <Typography flex="3" variant="body2">
                          {specificBroker.transactionType === "one-off"
                            ? "One-off"
                            : "Recurring"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1
                  }}
                >
                  <Typography variant="h5" sx={{ marginBottom: 2 }}>
                    Deal & Risk Information
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box display="flex" gap={2}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Priority
                      </Typography>
                      <Box flex="3">
                        <Chip
                          label={
                            specificBroker.urgent ? "URGENT" : "Not urgent"
                          }
                          size="small"
                          variant="filled"
                          sx={{
                            ...getPriorityColor(specificBroker.urgent)
                          }}
                        />
                      </Box>
                    </Box>

                    <Box display="flex" gap={2}>
                      <Typography
                        flex="1"
                        variant="caption"
                        color="text.secondary"
                      >
                        Expected Deal Volume
                      </Typography>
                      <Typography flex="3" variant="body2">
                        {specificBroker.dealVolume
                          ? `${specificBroker.dealVolume} ${specificBroker.unit || ""}`
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showEdit &&
        (isMerger ? (
          <EditMergerDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            kind="broker"
            entity={specificBroker}
          />
        ) : (
          <EditBrokerDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            specificBroker={specificBroker}
          />
        ))}
    </>
  );
};
